<template>
  <ReportCard :title="$t('report.members_overview_title')" header-margin="mb-4">
    <template #header>
      <button @click.stop="showOverviewInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.info_button_title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>

    <!-- Skeleton while loading -->
    <template v-if="loading">
      <div class="animate-pulse">
        <div class="text-center mb-4">
          <div class="h-9 w-24 mx-auto rounded-lg bg-muted/50"></div>
          <div class="h-3 w-32 mx-auto rounded bg-muted/40 mt-2"></div>
        </div>
        <div>
          <div class="h-2 w-full rounded-full bg-muted/40"></div>
          <div class="flex justify-between mt-2">
            <div class="h-3 w-24 rounded bg-muted/30"></div>
            <div class="h-3 w-24 rounded bg-muted/30"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Real content -->
    <template v-else>
      <!-- Hero: Total members -->
      <div class="text-center mb-4">
        <span class="text-[32px] font-extrabold text-foreground leading-none">
          {{ totalMembers }}
        </span>
        <div class="flex items-center justify-center gap-2 mt-1">
          <p class="text-[11px] text-muted-foreground">
            {{ $t('report.members_total') }}
          </p>
          <EvolutionIndicator v-if="totalMembersEvolution !== null"
            :value="totalMembersEvolution"
            :formatted="(totalMembersEvolution > 0 ? '+' : '') + totalMembersEvolution.toFixed(1) + ' %'"
            :show-neutral="true" />
        </div>
      </div>

      <!-- Active / Inactive bar -->
      <div v-if="activeCount + inactiveCount > 0">
        <div class="flex w-full h-2 rounded-full overflow-hidden">
          <div class="h-full transition-all duration-500"
            :style="{ width: activePct + '%', backgroundColor: '#10B981' }"></div>
          <div class="h-full transition-all duration-500"
            :style="{ width: inactivePct + '%', backgroundColor: '#94A3B8' }"></div>
        </div>
        <div class="flex justify-between mt-2">
          <div>
            <span class="text-[10px] font-semibold" style="color: #10B981">
              {{ $t('report.members_active') }}
            </span>
            <span class="text-[13px] font-bold text-foreground ml-1">{{ activeCount }}</span>
            <span class="text-[10px] text-muted-foreground ml-0.5">
              ({{ activePct.toFixed(0) }}%)
            </span>
          </div>
          <div>
            <span class="text-[10px] font-semibold" style="color: #94A3B8">
              {{ $t('report.members_inactive') }}
            </span>
            <span class="text-[13px] font-bold text-foreground ml-1">{{ inactiveCount }}</span>
            <span class="text-[10px] text-muted-foreground ml-0.5">
              ({{ inactivePct.toFixed(0) }}%)
            </span>
          </div>
        </div>
      </div>
    </template>
  </ReportCard>

  <BottomSheet v-model:is-open="showOverviewInfo" variant="sidebar">
    <MembersOverviewInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { MembersStatsResponse } from '../../responses'
import ReportCard from '../ReportCard.vue'
import EvolutionIndicator from '../ui/EvolutionIndicator.vue'
import BottomSheet from '../BottomSheet.vue'
import MembersOverviewInfoSheet from '../MembersOverviewInfoSheet.vue'

const props = defineProps<{
  response: MembersStatsResponse | null
  loading: boolean
}>()

const showOverviewInfo = ref(false)

const totalMembers = computed(() => {
  if (!props.response?.data) return 0
  return props.response.data.reduce((sum, d) => sum + (d.metrics.count ?? 0), 0)
})

const activeCount = computed(() => {
  const point = props.response?.data?.find(d => d.dimensions.active === 'true')
  return point?.metrics.count ?? 0
})

const inactiveCount = computed(() => {
  const point = props.response?.data?.find(d => d.dimensions.active === 'false')
  return point?.metrics.count ?? 0
})

const activePct = computed(() => {
  const total = activeCount.value + inactiveCount.value
  return total > 0 ? (activeCount.value / total) * 100 : 0
})

const inactivePct = computed(() => {
  const total = activeCount.value + inactiveCount.value
  return total > 0 ? (inactiveCount.value / total) * 100 : 0
})

const totalMembersEvolution = computed((): number | null => {
  const resp = props.response
  if (!resp?.intervals || !resp?.previous_intervals) return null
  if (!resp.equitable_period || !resp.equitable_previous_period) return null

  const eqEnd = new Date(resp.equitable_period.end).getTime()
  const eqPrevEnd = new Date(resp.equitable_previous_period.end).getTime()

  const currentTotal = resp.intervals
    .filter(iv => new Date(iv.date).getTime() <= eqEnd)
    .reduce((sum, iv) => sum + iv.data.reduce((s, d) => s + (d.metrics.count ?? 0), 0), 0)

  const prevTotal = resp.previous_intervals
    .filter(iv => new Date(iv.date).getTime() <= eqPrevEnd)
    .reduce((sum, iv) => sum + iv.data.reduce((s, d) => s + (d.metrics.count ?? 0), 0), 0)

  if (prevTotal === 0) return null
  return ((currentTotal - prevTotal) / prevTotal) * 100
})
</script>
